// Beslenme (2026-10-07) — Beslenme Planı, Besin Günlüğü (Takip), Su Takibi ve
// Beslenme Öyküsü tek sekmede. Üstte dört özet kutusu (tıklanınca o bölüm açılır),
// altında bölüm seçici; bölümlerin içeriği mevcut bileşenlerin aynısı.
// Bölüm görünürlüğü izin + modülle: plan/takip/öykü → diet, su → hydration.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { COLORS, CARD_PAGE_PAD } from "../../../styles/uiConstants";
import SummaryTile from "../../molecules/SummaryTile";
import { SegmentedControl } from "../../atoms";
import { useClinicTheme } from "../../../theme";
import {
  useListDietPlansByPatientMutation,
  useGetIntakeSummaryMutation,
  useListNutritionIntakesMutation,
} from "../../../store/secretaryApi";
import { useHydrationSummaryMutation } from "../../../store/hydrationApi";
import { relativePast } from "../../../pages/Secretary/PatientsPage/patientListFormat";
import DietTab from "./DietTab";
import FoodLogTab from "./FoodLogTab";
import HydrationTab from "./HydrationTab";
import NutritionIntakeTab from "./NutritionIntakeTab";

// Diyet modülü açıkken su ayrı bölüm DEĞİL: hedef Plan'da girilir, içilen su Takip'te
// görünür. Ayrı "Su" bölümü yalnız diyet modülü kapalı (yalnız su modülü) kliniklerde.
export const NUTRITION_SECTIONS = [
  { value: "plan", label: "Plan", needs: "diet" },
  { value: "takip", label: "Takip", needs: "dietApp" },
  { value: "su", label: "Su", needs: "hydrationOnly" },
  { value: "oyku", label: "Öykü", needs: "diet" },
];

/** Eski ayrı sekmelerin adresleri → yeni bölüm (eski bağlantılar çalışsın). */
export const LEGACY_NUTRITION_TABS = {
  diyet: "plan",
  "besin-gunlugu": "takip",
  "su-takibi": "su",
  "beslenme-oykusu": "oyku",
};

const kcal = (v) => `${Math.round(Number(v) || 0).toLocaleString("tr-TR")} kcal`;
const liters = (ml) => `${(Math.round((Number(ml) || 0) / 100) / 10).toLocaleString("tr-TR")} L`;
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const daysLeft = (endDate) => {
  if (!endDate) return null;
  const end = new Date(`${endDate}T23:59:59`);
  return Math.ceil((end - new Date()) / 86400000);
};

/**
 * @param {string} patientId
 * @param {object} patient
 * @param {string} token
 * @param {boolean} canEdit
 * @param {boolean} canSeeDiet
 * @param {boolean} canSeeHydration
 * @param {boolean} canManageHydration
 * @param {boolean} [usesApp=true] - hasta mobil uygulamaya girmiş mi; değilse hastanın
 *   kendi girdiği kayıtlar (Takip) gizlenir
 * @param {string|null} section - URL'deki bölüm (?bolum=)
 * @param {(s: string) => void} onSectionChange
 */
export default function NutritionTab({
  patientId,
  patient,
  token,
  canEdit,
  canSeeDiet,
  canSeeHydration,
  canManageHydration,
  usesApp = true,
  section,
  onSectionChange,
}) {
  const { palette } = useClinicTheme();
  const sections = useMemo(
    () =>
      NUTRITION_SECTIONS.filter((s) =>
        s.needs === "diet"
          ? canSeeDiet
          : s.needs === "dietApp"
            ? canSeeDiet && usesApp
            : canSeeHydration && !canSeeDiet
      ),
    [canSeeDiet, canSeeHydration, usesApp]
  );

  const [listPlans] = useListDietPlansByPatientMutation();
  const [getIntake] = useGetIntakeSummaryMutation();
  const [listIntakes] = useListNutritionIntakesMutation();
  const [getHydration] = useHydrationSummaryMutation();

  const [summary, setSummary] = useState(null);

  // Özet kutuları: bölüm değişince (içerikte plan/öykü eklenmiş olabilir) tazelenir.
  const loadSummary = useCallback(async () => {
    if (!token || !patientId) return;
    const safe = (p) => p.then((r) => r?.data ?? null).catch(() => null);
    const [plans, intake, intakes, hydration] = await Promise.all([
      canSeeDiet ? safe(listPlans({ token, patientId }).unwrap()) : null,
      canSeeDiet && usesApp ? safe(getIntake({ token, patientId, days: 1 }).unwrap()) : null,
      canSeeDiet ? safe(listIntakes({ token, patientId }).unwrap()) : null,
      canSeeHydration ? safe(getHydration({ token, patientId, days: 7 }).unwrap()) : null,
    ]);
    setSummary({
      plan: (plans?.plans || []).find((p) => p.status === "active") || null,
      intake,
      lastIntake: (intakes?.intakes || [])[0] || null,
      hydration,
    });
  }, [token, patientId, canSeeDiet, canSeeHydration, usesApp, listPlans, getIntake, listIntakes, getHydration]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, section]);

  // Bölüm yoksa / görünmüyorsa: aktif plan varsa Plan, yoksa (ilk görüşme) Öykü; diyet yoksa Su.
  const visible = (v) => sections.some((s) => s.value === v);
  // Eski "Su" adresi diyet modülü açıkken Takip'e düşer (su orada)
  let current = visible(section) ? section : section === "su" && visible("takip") ? "takip" : null;
  if (!current) {
    if (!canSeeDiet) current = "su";
    else if (!summary) current = null;
    else current = summary.plan ? "plan" : "oyku";
  }
  useEffect(() => {
    if (current && current !== section) onSectionChange(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const loading = !summary;
  const plan = summary?.plan;
  const today = (summary?.intake?.days || []).find((d) => d.date === todayYmd());
  const target = summary?.intake?.adherence?.target;
  const hyd = summary?.hydration;
  const left = daysLeft(plan?.endDate);

  const tiles = [
    canSeeDiet && {
      key: "plan",
      label: "Aktif plan",
      value: plan ? plan.title || "Beslenme planı" : "Aktif plan yok",
      hint: plan
        ? [
            left != null ? (left >= 0 ? `${left} gün kaldı` : "Süresi geçti") : "Bitiş tarihi yok",
            plan.targets?.waterMl ? `Su ${liters(plan.targets.waterMl)}` : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : canEdit
          ? "Plan oluşturmak için tıklayın"
          : "",
    },
    canSeeDiet && usesApp && {
      key: "takip",
      label: "Bugün",
      value: !plan
        ? "Plan olunca görünür"
        : today?.logged
          ? `${kcal(today.calories)}${target ? ` / ${kcal(target)}` : ""}`
          : "Bugün kayıt yok",
      progress: plan && today?.logged && target ? (today.calories / target) * 100 : null,
      hint: [
        plan && today?.plannedMeals ? `${today.eatenMeals}/${today.plannedMeals} öğün yendi` : null,
        canSeeHydration && hyd ? `Su ${liters(hyd.today?.ml)} / ${liters(hyd.plan?.goalMl)}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    },
    canSeeHydration && !canSeeDiet && {
      key: "su",
      label: "Su (bugün)",
      value: hyd ? `${liters(hyd.today?.ml)} / ${liters(hyd.plan?.goalMl)}` : "—",
      progress: hyd?.plan?.goalMl ? ((hyd.today?.ml || 0) / hyd.plan.goalMl) * 100 : null,
      hint: hyd ? (hyd.plan?.reminders?.enabled ? "Hatırlatma açık" : "Hatırlatma kapalı") : "",
    },
    canSeeDiet && {
      key: "oyku",
      label: "Beslenme öyküsü",
      value: summary?.lastIntake
        ? relativePast(summary.lastIntake.updatedAt || summary.lastIntake.createdAt)
        : "Henüz yok",
      hint: summary?.lastIntake ? "Son güncelleme" : canEdit ? "İlk görüşmede doldurun" : "",
    },
  ].filter(Boolean);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 1.5, md: 2 }, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate200}` }}>
        {/* Masaüstü: özet kutuları seçici görevi görür. Mobil: yalnız bölüm seçici (ikisi birlikte değil). */}
        <Box
          sx={{
            display: { xs: "none", md: "grid" },
            gap: 1.5,
            gridTemplateColumns: { md: "1fr 1fr", lg: `repeat(${tiles.length}, minmax(0, 1fr))` },
          }}
        >
          {tiles.map((t) => (
            <SummaryTile
              key={t.key}
              {...t}
              loading={loading}
              active={current === t.key}
              onClick={() => onSectionChange(t.key)}
              palette={palette}
            />
          ))}
        </Box>
        {sections.length > 1 ? (
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <SegmentedControl
              value={current || ""}
              onChange={onSectionChange}
              options={sections.map(({ value, label }) => ({ value, label }))}
              ariaLabel="Beslenme bölümü"
              isLoading={!current}
              palette={palette}
            />
          </Box>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {current === "plan" && (
          <DietTab mode="standalone" patientId={patientId} patient={patient} token={token} canEdit={canEdit} />
        )}
        {current === "takip" && (
          <FoodLogTab
            patientId={patientId}
            patient={patient}
            token={token}
            hydrationSlot={
              // Su hedefi Plan'da girilir; burada yalnız hastanın içtiği su (hedef salt okunur)
              canSeeHydration ? (
                <HydrationTab patientId={patientId} token={token} canManage={false} planManaged />
              ) : null
            }
          />
        )}
        {current === "oyku" && (
          <NutritionIntakeTab patientId={patientId} patient={patient} token={token} canEdit={canEdit} />
        )}
        {current === "su" && (
          // Su bölümü: diğer bölümlerle aynı beyaz zemin
          <Box sx={{ ...CARD_PAGE_PAD, bgcolor: COLORS.white, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <HydrationTab patientId={patientId} token={token} canManage={canManageHydration} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
